import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { purchaseOrderActions } from "../slices/purchaseOrder";
import { getErrorMessage } from "../../utils/errorUtils";

import { PurchaseOrderRequestDto } from "../../shared/dtos/purchaseOrder/PurchaseOrderRequestDto";
import { PurchaseOrderConfirmRequestDto } from "../../shared/dtos/purchaseOrder/PurchaseOrderConfirmRequestDto";
import { PurchaseOrderReceiveRequestDto } from "../../shared/dtos/purchaseOrder/PurchaseOrderReceiveRequestDto";
import { PaymentRequestDto } from "../../shared/dtos/payment/PaymentRequestDto";
import { PurchaseOrderResponseDto } from "../../shared/dtos/purchaseOrder/PurchaseOrderResponseDto";
import { PurchaseOrderSummaryDto } from "../../shared/dtos/purchaseOrder/PurchaseOrderSummaryDto";
import { PurchaseOrderQueryParams } from "../../shared/dtos/purchaseOrder/PurchaseOrderQueryParams";
import { EmailRequestDto } from "../../shared/dtos/common/EmailRequestDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export interface POProductStub {
  id: string;
  name: string;
  unitCost: number;
  category?: string;
}

export const getPurchaseOrders =
  (params?: PurchaseOrderQueryParams) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(purchaseOrderActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<PurchaseOrderSummaryDto>>
      >("/purchase-orders", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(purchaseOrderActions.setItems(data.data.content));
        dispatch(purchaseOrderActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(purchaseOrderActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getPurchaseOrderById =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(purchaseOrderActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PurchaseOrderResponseDto>
      >(`/purchase-orders/${id}`, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(purchaseOrderActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(purchaseOrderActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const createPurchaseOrder =
  (payload: PurchaseOrderRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(purchaseOrderActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<PurchaseOrderResponseDto>
      >("/purchase-orders", payload, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Purchase order created successfully!");
        dispatch(purchaseOrderActions.clearPoCart());
        dispatch(
          purchaseOrderActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(purchaseOrderActions.setErrorCreate(message));
    }
  };

export const confirmPurchaseOrder =
  (id: string, payload: PurchaseOrderConfirmRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(purchaseOrderActions.setLoadingConfirm());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<PurchaseOrderResponseDto>
      >(`/purchase-orders/confirm/${id}`, payload, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Order confirmed!");
        dispatch(purchaseOrderActions.actionSuccess(data.data));
        dispatch(
          purchaseOrderActions.setServerStatus({
            operation: "confirm",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(purchaseOrderActions.setErrorConfirm(message));
    }
  };

export const receivePurchaseOrderItems =
  (id: string, payload: PurchaseOrderReceiveRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(purchaseOrderActions.setLoadingReceive());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<PurchaseOrderResponseDto>
      >(`/purchase-orders/receive/${id}`, payload, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Items received successfully!");
        dispatch(purchaseOrderActions.actionSuccess(data.data));
        dispatch(
          purchaseOrderActions.setServerStatus({
            operation: "receive",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(purchaseOrderActions.setErrorReceive(message));
    }
  };

export const payPurchaseOrder =
  (id: string, payload: PaymentRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(purchaseOrderActions.setLoadingPay());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<PurchaseOrderResponseDto>
      >(`/purchase-orders/payments/${id}`, payload, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Payment recorded successfully!");
        dispatch(purchaseOrderActions.actionSuccess(data.data));
        dispatch(
          purchaseOrderActions.setServerStatus({
            operation: "pay",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(purchaseOrderActions.setErrorPay(message));
    }
  };

export const getPurchaseOrderPdf =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(purchaseOrderActions.setLoadingPdf());
    try {
      const { data } = await axiosInstance.get<Blob>(
        `/purchase-orders/pdf/${id}`,
        {
          headers: { requiresAuth: true },
          responseType: "blob",
        },
      );

      const pdfBlobUrl = window.URL.createObjectURL(
        new Blob([data], { type: "application/pdf" }),
      );
      dispatch(purchaseOrderActions.setPdfUrl(pdfBlobUrl));
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error("Failed to load PDF preview: " + message);
      dispatch(purchaseOrderActions.setErrorPdf(message));
    }
  };

export const emailPurchaseOrder =
  (id: string, emailPayload: EmailRequestDto) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    dispatch(purchaseOrderActions.setLoadingEmail());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        `/purchase-orders/email/${id}`,
        emailPayload,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Purchase Order emailed successfully!");
        dispatch(purchaseOrderActions.setEmailSuccess());
        return true;
      }
      return false;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(purchaseOrderActions.setErrorEmail(message));
      return false;
    }
  };

export const resetPurchaseOrderServerStatus =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(purchaseOrderActions.setServerStatus(null));
  };

// --- Cart Actions ---
export const addPoItem =
  (product: POProductStub) =>
  (dispatch: AppDispatch): void => {
    dispatch(
      purchaseOrderActions.poItemAdd({
        inventoryItemId: product.id,
        name: product.name,
        category: product.category,
        unitCost: product.unitCost,
        quantity: 1,
      }),
    );
  };

export const updatePoItemFields =
  (inventoryItemId: string, updates: any) =>
  (dispatch: AppDispatch): void => {
    dispatch(
      purchaseOrderActions.poItemUpdateFields({ inventoryItemId, updates }),
    );
  };

export const poItemRemove =
  (inventoryItemId: string) => (dispatch: AppDispatch) =>
    dispatch(purchaseOrderActions.poItemRemove(inventoryItemId));
export const setPoSupplier = (supplier: any) => (dispatch: AppDispatch) =>
  dispatch(purchaseOrderActions.setPoSupplier(supplier));
export const clearPoCart = () => (dispatch: AppDispatch) =>
  dispatch(purchaseOrderActions.clearPoCart());

export const resetPOServerStatus =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(purchaseOrderActions.setServerStatus(null));
  };
